/**
 * Adapter for the Mauvely design system bundle.
 *
 * `_ds_bundle.js` is a prebuilt IIFE that reads a bare `React` global and
 * assigns 18 components onto `window.MauvelyDesignSystem_227a0c`. The `.jsx`
 * sources it was compiled from were not part of the design handoff, so the
 * bundle is kept as the component layer rather than rewritten — reimplementing
 * 18 components blind would drift from a design that has already been signed off.
 *
 * Two consequences, both handled in `loadDesignSystem`:
 *
 *   · React must exist on `window` BEFORE the bundle runs. We publish the very
 *     same React instance the app imports, so there is exactly one copy and
 *     hooks work across the boundary. (Loading React separately from a CDN —
 *     which is what the design prototype did — would give two instances and
 *     break every hook in a DS component.)
 *   · The Icon component reads `window.lucide`. That global is a name the
 *     bundle chose, and what this app puts in it is the **Phosphor** set the
 *     desktop app draws (`icons.ts`), so the two clients share a hand.
 */

import React from 'react';
import * as ReactDOM from 'react-dom';
import { createElement as iconElement, iconSet } from './icons';

const BUNDLE_URL = '/_ds/mauvely/_ds_bundle.js';

type C<P = Record<string, unknown>> = React.ComponentType<P>;

export interface DesignSystem {
  Badge: C<{
    tone?: 'neutral' | 'primary' | 'accent' | 'signal' | 'success' | 'warning' | 'danger';
    icon?: string;
    dot?: boolean;
    mono?: boolean;
    children?: React.ReactNode;
    style?: React.CSSProperties;
  }>;
  Button: C<{
    // The bundle's real variant list. Note there is NO `outline` — the design
    // prototype passed `variant="outline"` throughout, which fell through to the
    // `primary` default and rendered every secondary CTA as a filled violet
    // button. `secondary` (transparent, bordered) is what those call sites meant,
    // and is what restores the intended hierarchy on the pricing cards.
    variant?: 'primary' | 'secondary' | 'ghost' | 'soft' | 'signal' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    fullWidth?: boolean;
    disabled?: boolean;
    type?: 'button' | 'submit';
    /** Renders an `<a>` instead of a `<button>`, with the same skin. */
    href?: string;
    onClick?: (e: React.MouseEvent) => void;
    children?: React.ReactNode;
    style?: React.CSSProperties;
  }>;
  Card: C<{
    as?: string;
    padding?: 'none' | 'compact' | 'md' | 'panel';
    tone?: 'surface' | 'sunken';
    interactive?: boolean;
    radius?: number | string;
    onClick?: (e: React.MouseEvent) => void;
    children?: React.ReactNode;
    style?: React.CSSProperties;
  }>;
  Icon: C<{ name: string; size?: number; strokeWidth?: number; color?: string; style?: React.CSSProperties }>;
  IconButton: C<{
    /** A key in `icons.ts`. A name with no entry renders nothing at all rather
     *  than throwing, so grep that file first when a button goes blank. */
    icon: string;
    /** The accessible name, and the only one the button has — it renders no
     *  text. Never omit it. */
    label: string;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'ghost' | 'primary' | 'soft' | 'danger';
    active?: boolean;
    disabled?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    style?: React.CSSProperties;
  }>;
  Stat: C<{
    label?: string;
    value?: React.ReactNode;
    tone?: 'strong' | 'primary' | 'signal';
    size?: 'sm' | 'md' | 'lg';
    style?: React.CSSProperties;
  }>;
  Tag: C<{
    selected?: boolean;
    onRemove?: () => void;
    onClick?: (e: React.MouseEvent) => void;
    children?: React.ReactNode;
    style?: React.CSSProperties;
  }>;
  Wordmark: C<{
    lockup?: 'primary' | 'reversed' | 'mono';
    size?: number;
    showDot?: boolean;
    style?: React.CSSProperties;
  }>;
  Dialog: C<Record<string, unknown>>;
  Progress: C<{
    value?: number;
    label?: string;
    showValue?: boolean;
    size?: 'sm' | 'md' | 'lg';
    tone?: 'signal' | 'primary' | 'accent';
    style?: React.CSSProperties;
  }>;
  Toast: C<Record<string, unknown>>;
  Tooltip: C<{
    content?: React.ReactNode;
    side?: 'top' | 'bottom' | 'left' | 'right';
    children?: React.ReactNode;
    style?: React.CSSProperties;
  }>;
  Checkbox: C<{
    label?: string;
    description?: string;
    checked?: boolean;
    defaultChecked?: boolean;
    disabled?: boolean;
    // NOT a change event. The bundle renders a `<span role="checkbox">` rather
    // than an `<input>`, and calls `onChange(!on, e)` — the new value first.
    // This was declared as a ChangeEvent, so `e.target.checked` typechecked and
    // then threw at runtime, which is why the pin checkbox appeared dead.
    onChange?: (checked: boolean, e: React.SyntheticEvent) => void;
  }>;
  Input: C<{
    label?: string;
    type?: string;
    placeholder?: string;
    hint?: string;
    value?: string;
    defaultValue?: string;
    autoComplete?: string;
    multiline?: boolean;
    rows?: number;
    disabled?: boolean;
    // The message, not a flag. It reds the border *and* replaces `hint`, so a
    // caller passing both gets the error. This was declared as `invalid?:
    // boolean`, which the bundle does not destructure — it landed in the
    // `...rest` it spreads onto the `<input>`, so React warned about a
    // non-boolean DOM attribute and the red border never appeared.
    error?: string;
    // Both real, both destructured by the bundle, and both missing from this
    // declaration until the list's filter field asked for them. `icon` is a
    // name in `icons.ts`, drawn inside the field at its leading edge.
    icon?: string;
    size?: 'sm' | 'md' | 'lg';
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    // Both land in the `...rest` the bundle spreads onto the field itself.
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    autoFocus?: boolean;
    style?: React.CSSProperties;
  }>;
  Radio: C<Record<string, unknown>>;
  Select: C<{
    label?: string;
    value?: string;
    options?: { value: string; label: string }[];
    onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    style?: React.CSSProperties;
  }>;
  Switch: C<{
    label?: string;
    description?: string;
    checked?: boolean;
    defaultChecked?: boolean;
    disabled?: boolean;
    /** Same shape as Checkbox above: `(nextValue, event)`, not a change event. */
    onChange?: (checked: boolean, e: React.SyntheticEvent) => void;
    /** Spread onto the `<label>` the bundle wraps the row in, after its own —
     *  which is what lets a settings row put the toggle on the right. */
    style?: React.CSSProperties;
  }>;
  Tabs: C<{
    items?: ({ value: string; label: string } | string)[];
    value?: string;
    defaultValue?: string;
    onChange?: (value: string) => void;
    variant?: 'underline' | 'pill';
    style?: React.CSSProperties;
  }>;
  /** The bundle collects per-component init failures here instead of throwing. */
  __errors?: unknown[];
}

declare global {
  interface Window {
    React?: typeof React;
    ReactDOM?: typeof ReactDOM;
    lucide?: { icons: unknown; createElement: unknown };
    MauvelyDesignSystem_227a0c?: DesignSystem;
  }
}

let loaded: Promise<DesignSystem> | null = null;

export function loadDesignSystem(): Promise<DesignSystem> {
  if (loaded) return loaded;

  loaded = new Promise<DesignSystem>((resolve, reject) => {
    // Order matters: the bundle reads these at evaluation time.
    window.React = React;
    window.ReactDOM = ReactDOM;
    // The name is the bundle's, not ours: its `Icon` reads `window.lucide`.
    // What it finds there is Phosphor — see `icons.ts`.
    window.lucide = { icons: iconSet, createElement: iconElement };

    const script = document.createElement('script');
    script.src = BUNDLE_URL;
    script.async = false;

    script.onload = () => {
      const ns = window.MauvelyDesignSystem_227a0c;
      if (!ns) {
        reject(new Error('Design system bundle loaded but did not register its namespace.'));
        return;
      }
      // The bundle wraps each component in try/catch and pushes failures here.
      // Surface them — a silently missing component renders as nothing at all,
      // which is far harder to diagnose later.
      if (ns.__errors?.length) {
        console.error('Design system components failed to initialise:', ns.__errors);
      }
      resolve(ns);
    };

    script.onerror = () => reject(new Error(`Could not load ${BUNDLE_URL}`));
    document.head.appendChild(script);
  });

  return loaded;
}

/**
 * Synchronous accessor for render code. Safe because the app does not mount
 * until `loadDesignSystem()` resolves — see main.tsx.
 */
export function ds(): DesignSystem {
  const ns = window.MauvelyDesignSystem_227a0c;
  if (!ns) throw new Error('Design system used before it finished loading.');
  return ns;
}
